import React, { useState, useRef, useEffect } from "react";
import { Toast } from 'primereact/toast';
import { DataTableStateEvent } from "primereact/datatable";
import { Button } from "primereact/button";
import AppLoading from "../app-loading/app-loading";
import CustomerFollowUpSearch from "./customer-followup-search";
import CustomerFollowUpDatatable from "./customer-followup-datatable";
import editUserServices from "../../services/edit-users/edit-users.service";
import { CustomerFollowup, CustomerFollowupParams } from "../../models/orders/order-list-model";

const CustomerFollowUpMain: React.FC = () => {
    const toast = useRef<Toast>(null);
    const [loading, setLoading] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [dataList, setDataList] = useState<CustomerFollowup[]>([]);
    const [hasSearched, setHasSearched] = useState(false);
    const [maxRecentOrderAmount, setMaxRecentOrderAmount] = useState(10000); 

    const currentFilters = useRef<CustomerFollowupParams>({
        priority: null,
        routeCodeName: null,
        currentMonth: null,
        showNullStatusOnly: false,
        salesExecutiveID: null,
        userID: null,
        daysSinceRecentOrderMin: null,
        daysSinceRecentOrderMax: null,
        recentOrderAmountMin: null,
        recentOrderAmountMax: null,
        lastCalledStartDate: null,
        lastCalledEndDate: null,
        pageNumber: 1,
        pageSize: 10,
        sortColumn: '',
        sortDirection: ''
    });

    const fetchCustomerFollowups = async () => {
        setLoading(true);
        try {
            console.log('Sending filters to API:', currentFilters.current);
            const response = await editUserServices.getCustomerFollowup(currentFilters.current);
            
            if (response.isSuccess) {
                setDataList(response.followups || []);
                setTotalRecords(response.totalRecords || 0);
                setMaxRecentOrderAmount(response.maxRecentOrderAmount || 10000);
            } else {
                toast.current?.show({
                    severity: 'warn',
                    summary: 'Warning',
                    detail: response.message || 'No data found',
                    life: 3000
                });
                setDataList([]);
                setTotalRecords(0);
            }
        } catch (error) {
            console.error("Error fetching customer followups:", error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to fetch customer followups',
                life: 3000
            });
            setDataList([]);
            setTotalRecords(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCustomerFollowups();
    }, []);

    const handleSearch = async (filters: {
        priority?: string | null;
        routeCodeName?: string | null;
        currentMonth?: string | null;
        showNullStatusOnly?: boolean;
        salesExecutiveID?: number | null;
        userID?: number | null;
        daysSinceRecentOrderMin?: number | null;
        daysSinceRecentOrderMax?: number | null;
        recentOrderAmountMin?: number | null;
        recentOrderAmountMax?: number | null;
        lastCalledStartDate?: string | null;
        lastCalledEndDate?: string | null;
    }) => {
        currentFilters.current = {
            ...currentFilters.current,
            ...filters,
            pageNumber: 1,
        };
        setPageNumber(1);
        await fetchCustomerFollowups();
        setHasSearched(true);
    };

    const handleSortChange = async (field: string, direction: string) => {
        currentFilters.current = {
            ...currentFilters.current,
            sortColumn: field,
            sortDirection: direction,
            pageNumber: 1
        };
        setPageNumber(1);
        await fetchCustomerFollowups();
    };

    const handleReset = async () => {
        currentFilters.current = {
            priority: null,
            routeCodeName: null,
            currentMonth: null,
            showNullStatusOnly: false,
            salesExecutiveID: null,
            userID: null,
            daysSinceRecentOrderMin: null,
            daysSinceRecentOrderMax: null,
            recentOrderAmountMin: null,
            recentOrderAmountMax: null,
            lastCalledStartDate: null,
            lastCalledEndDate: null,
            sortColumn: '',
            sortDirection: '',
            pageNumber: 1,
            pageSize: pageSize
        };
        
        setPageNumber(1);
        setDataList([]);
        setTotalRecords(0);
        setHasSearched(false);
        await fetchCustomerFollowups();
    };

    const handlePageChange = (event: DataTableStateEvent) => {
        const newPageNumber = Math.floor((event.first || 0) / (event.rows || 10)) + 1;
        const newPageSize = event.rows || 10;
        setPageNumber(newPageNumber);
        setPageSize(newPageSize);

        currentFilters.current = {
            ...currentFilters.current,
            pageNumber: newPageNumber,
            pageSize: newPageSize
        };

        fetchCustomerFollowups();
    };

    return (
        <div className="layout-content mt-3 mb-3 grid grid-table-mobile">
            <Toast ref={toast} />

            <div className="col-12">
                <div className="flex align-items-center justify-content-between flex-wrap gap-2 w-full card-container mb-0">
                    <h5 className="ml-1 mb-0">Customer Follow-ups</h5>
                    <span>
                        <Button
                            className="text-black border-round mr-2"
                            icon="pi pi-file-export mr-1"
                            text
                            raised
                            disabled={!hasSearched || dataList.length === 0}
                            onClick={() => {
                                // Export logic goes here
                            }}
                        >
                            Export Customer Follow-ups
                        </Button>
                    </span>
                </div>
            </div>

            <CustomerFollowUpSearch
                onSearch={handleSearch}
                onReset={handleReset}
                maxRecentOrderAmount={maxRecentOrderAmount}
            />

            <CustomerFollowUpDatatable
                data={dataList}
                pageNumber={pageNumber}
                pageSize={pageSize}
                totalRecords={totalRecords}
                onPageChange={handlePageChange}
                refreshData={fetchCustomerFollowups}
                onSortChange={handleSortChange}
            />

            {loading && <AppLoading />}
        </div>
    );
};

export default CustomerFollowUpMain;